import { FlowTableLevel, FlowTableRow, ROW_TYPE } from "./flow-table-row.model";
import { sum } from "../../shared/utils/numbers";

export interface CompanyData {
  info: CompanyInfo;
  businessYears: BusinessYear[];
  accounts: Account[];
  accountNumberList: number[];
  accountHierarchy: AccountNode;
  currencies: CurrencyExchangeFactor[];
}

export interface CurrencyExchangeFactor {
  currencyId: number;
  name: string;
  factor: number;
}

export interface Account {
  id: number;
  number: number;
  name: string;
  groupId: number;
}

export interface CompanyInfo {
  id: number | null;
  name: string | null;
  tvaId: string | null;
}

export interface BusinessYear {
  id: number;
  start: string;
  end: string;
  status: BusinessYearStatus;
}

export const BUSINESS_YEAR_STATUS = {
  open: "open",
  locked: "locked",
  closed: "closed",
} as const;

export type BusinessYearStatus =
  (typeof BUSINESS_YEAR_STATUS)[keyof typeof BUSINESS_YEAR_STATUS];

export interface AccountState {
  lastRefreshed?: string;
  [accountNumber: number]: number;
}

export interface MonthlyAccountState {
  [yearMonth: string]: AccountState;
}

export enum AccountCategory {
  operating = "operating",
  investing = "investing",
  financing = "financing",
  treasury = "treasury",
}

export enum AccountType {
  input = "input",
  output = "output",
  both = "both",
  variation = "variation",
}

export const ACCOUNTING_MODE = {
  operating: "operating",
  investing: "investing",
  financing: "financing",
} as const;
export type AccountingMode =
  (typeof ACCOUNTING_MODE)[keyof typeof ACCOUNTING_MODE];

export interface AccountInfo {
  number: number;
  name: string;
  cat: AccountCategory;
  type: AccountType;
}

export interface AccountValue {
  accountNumber: string;
  value: number;
}

export interface MonthlyAccount {
  yearMonth: string;
  accountValue: AccountValue;
}

export interface MonthlyAccounts {
  yearMonth: string;
  accountsValues: AccountValue[];
}

export interface MonthlyCashFlow {
  yearMonth: string;
  cashIn: number;
  cashOut: number;
  result: number;
  treasury: number;
}

export interface AccountNode {
  info: AccountInfo;
  children: AccountNode[];
}

export const accountTypeLookup = {
  1: AccountType.both,
  2: AccountType.both,
  3: AccountType.input,
  4: AccountType.output,
  5: AccountType.output,
  6: AccountType.output,
  7: AccountType.both,
  8: AccountType.both,
};

const getAccountType = (info: AccountInfo): AccountType => {
  const firstDigit = Number(info.number.toString().charAt(0));
  return (
    accountTypeLookup[firstDigit as keyof typeof accountTypeLookup] ||
    AccountType.variation
  );
};

export const getAccountLevel = (info: AccountInfo): FlowTableLevel => {
  const index = info.number.toString().length - 1;
  const standardizedIndex = Math.min(3, index);
  return Object.values(FlowTableLevel)[standardizedIndex];
};

export const createRowNode = (
  accountNode: AccountNode,
  depth: number,
  monthlyAccounts: MonthlyAccounts[],
  openedAccounts: number[],
): FlowTableRow => {
  const info = accountNode.info;
  if (accountNode.children.length === 0) {
    let values: number[] = Array(monthlyAccounts.length).fill(0);
    if (accountNode.info.number >= 1000) {
      values = monthlyAccounts.map(
        (accounts) =>
          accounts.accountsValues.find(
            (account) =>
              account.accountNumber === accountNode.info.number.toString(),
          )?.value ?? 0,
      );
    }
    return createTableRow(
      info.name,
      info.number,
      info.cat,
      getAccountType(info),
      getAccountLevel(info),
      values,
      [],
      depth,
      info.number < 1 || openedAccounts.includes(info.number),
    );
  } else {
    const children = accountNode.children.map((node) =>
      createRowNode(node, depth + 1, monthlyAccounts, openedAccounts),
    );
    // TODO here values have to be added or subtracted properly when reaching depth = 0
    const values = children.reduce((acc: number[], cur) => {
      const sign =
        depth > 1 || isCashInAccount(cur.accountNo.toString()) ? 1 : -1;
      return cur.values.map((value, index) => acc[index] + sign * value);
    }, Array(monthlyAccounts.length).fill(0));
    return createTableRow(
      info.name,
      info.number,
      info.cat,
      getAccountType(info),
      getAccountLevel(info),
      values,
      children,
      depth,
      info.number < 1 || openedAccounts.includes(info.number),
    );
  }
};

export const isCashInAccount = (accountNum: string): boolean =>
  Number(accountNum.charAt(0)) === 3;

const createTableRow = (
  name: string,
  accountNo: number,
  cat: AccountCategory,
  type: AccountType,
  level: FlowTableLevel,
  values: number[],
  children: FlowTableRow[],
  depth: number,
  opened: boolean,
) => ({
  name,
  accountNo,
  cat,
  type,
  level,
  opened,
  color: null,
  values: values,
  total: sum(values),
  children,
  depth,
  rowType: depth === 1 ? ROW_TYPE.heading : ROW_TYPE.node,
  percent: false,
});
